import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../auth/session';
import { useAuth } from '../auth/AuthContext';

type StoreModule = {
  id: number;
  code: string;
  name: string;
  plugin_id: string | null;
  description: string | null;
  icon: string | null;
  price_monthly: number;
  price_annual: number;
  is_sellable: boolean;
  sort_order: number;
  is_active: boolean;
};

type BillingCycle = 'monthly' | 'annual';

// Map plugin_id → vertical category code
const PLUGIN_TO_CATEGORY: Record<string, string> = {
  restaurant: 'restaurant',
  beauty: 'beauty',
  gym: 'gym',
};

// Category labels (match tenant_categories table)
const CATEGORY_LABELS: Record<string, string> = {
  restaurant: 'Ristorante',
  beauty: 'Estetica',
  gym: 'Palestra',
};

// Plugin labels for grouping
const PLUGIN_LABELS: Record<string, string> = {
  gym: 'Gym & Fitness',
  beauty: 'Beauty & Wellness',
  restaurant: 'Restaurant',
  haccp: 'HACCP',
  support: 'Support',
  portale: 'Portale',
  hello: 'Hello Demo',
  other: 'Altri',
};

export function TenantStorePage() {
  const { refreshEntitlements } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modules, setModules] = useState<StoreModule[]>([]);
  const modulesRef = useRef<StoreModule[]>([]);
  const [tenantCategory, setTenantCategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('__auto');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  async function load() {
    const res = await apiFetch('/api/v1/tenant/store/modules');
    const body = (await res.json().catch(() => ({}))) as {
      modules?: StoreModule[];
      tenant_category?: string | null;
      message?: string;
      error?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `store ${res.status}`);
    const newModules = Array.isArray(body.modules) ? body.modules : [];
    modulesRef.current = newModules;
    setModules(newModules);
    setTenantCategory(body.tenant_category || null);
    // Default: auto-filter by tenant category
    if (selectedCategory === '__auto' && body.tenant_category) {
      setSelectedCategory(body.tenant_category);
    }
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Stripe redirect back to store page
  useEffect(() => {
    const status = searchParams.get('status');
    const moduleName = searchParams.get('module');
    if (status === 'success') {
      // Sync with backend — verify the module was activated via webhook
      // If webhook hasn't fired yet, the sync endpoint will activate it
      void (async () => {
        try {
          if (moduleName) {
            await apiFetch('/api/v1/tenant/store/sync', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ module_code: moduleName }),
            });
          }
          await load();
          await refreshEntitlements();
          // Verify the module is actually active before claiming success
          const updatedMod = modulesRef.current.find((m) => m.code === moduleName);
          if (moduleName && updatedMod && !updatedMod.is_active) {
            setMessage(
              `Pagamento ricevuto. Il modulo «${moduleName}» è in fase di attivazione. Aggiorna la pagina tra qualche secondo.`
            );
          } else {
            setMessage(
              moduleName
                ? `Pagamento completato! Il modulo «${moduleName}» è stato attivato.`
                : 'Pagamento completato! Il modulo è stato attivato.'
            );
          }
        } catch {
          setMessage('Pagamento completato. Ricarica la pagina per vedere il modulo attivo.');
        }
      })();
      setSearchParams({}, { replace: true });
    } else if (status === 'cancel') {
      setError('Pagamento annullato. Il modulo non è stato attivato.');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Available categories derived from modules
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const m of modules) {
      const cat = m.plugin_id ? PLUGIN_TO_CATEGORY[m.plugin_id] : null;
      if (cat) cats.add(cat);
    }
    return Array.from(cats).sort();
  }, [modules]);

  // Filter modules by selected category
  const filteredModules = useMemo(() => {
    if (selectedCategory === '__all') return modules;
    return modules.filter((m) => {
      const cat = m.plugin_id ? PLUGIN_TO_CATEGORY[m.plugin_id] : null;
      // Support is cross-vertical: always visible
      if (m.plugin_id === 'support') return true;
      // HACCP is food-safety: only for restaurant category
      if (m.plugin_id === 'haccp') return selectedCategory === 'restaurant';
      return cat === selectedCategory;
    });
  }, [modules, selectedCategory]);

  // Group filtered modules by plugin_id
  const grouped = useMemo(() => {
    return filteredModules.reduce<Record<string, StoreModule[]>>((acc, m) => {
      const key = m.plugin_id || 'other';
      if (!acc[key]) acc[key] = [];
      acc[key].push(m);
      return acc;
    }, {});
  }, [filteredModules]);

  async function onSubscribe(mod: StoreModule) {
    setBusy(mod.code);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch('/api/v1/tenant/store/checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          module_code: mod.code,
          billing_cycle: cycle,
          success_url: `${window.location.origin}/tenant/store?status=success&module=${mod.code}`,
          cancel_url: `${window.location.origin}/tenant/store?status=cancel`,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        url?: string;
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (body.url) {
        window.location.href = body.url;
      } else {
        // Offline mode — module activated immediately
        setMessage(`Modulo «${mod.name}» attivato!`);
        await load();
        await refreshEntitlements();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function onCancel(mod: StoreModule) {
    if (!window.confirm(`Disdire il modulo «${mod.name}»? Il servizio resterà attivo fino alla fine del periodo pagato.`)) return;
    setBusy(mod.code);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch('/api/v1/tenant/store/cancel', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ module_code: mod.code }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Modulo «${mod.name}» disdetto.`);
      await load();
      await refreshEntitlements();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const hasCategory = tenantCategory != null;
  const showingFiltered = selectedCategory !== '__all';

  return (
    <section className="sk-saas-page">
      <header className="sk-saas-page__header">
        <div></div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/tenant/modules" className="sk-btn sk-btn--quiet">
            I miei moduli
          </Link>
          <div style={{ display: 'inline-flex', border: '1px solid var(--sk-border)', borderRadius: '0.4rem', overflow: 'hidden' }}>
            <button
              type="button"
              className={`sk-btn ${cycle === 'monthly' ? '' : 'sk-btn--quiet'}`}
              style={{ borderRadius: 0 }}
              onClick={() => setCycle('monthly')}
            >
              Mensile
            </button>
            <button
              type="button"
              className={`sk-btn ${cycle === 'annual' ? '' : 'sk-btn--quiet'}`}
              style={{ borderRadius: 0 }}
              onClick={() => setCycle('annual')}
            >
              Annuale (2 mesi gratis)
            </button>
          </div>
        </div>
      </header>

      {/* Category filter bar */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '0.75rem 1rem',
        background: 'var(--sk-panel-strong)',
        border: '1px solid var(--sk-border)',
        borderRadius: 'var(--sk-radius)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Categoria:</span>
          <select
            className="sk-input"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ minWidth: '12rem' }}
          >
            {hasCategory ? (
              <option value={tenantCategory!}>
                {CATEGORY_LABELS[tenantCategory!] || tenantCategory} (consigliata)
              </option>
            ) : null}
            {availableCategories
              .filter((c) => c !== tenantCategory)
              .map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat] || cat}
                </option>
              ))}
            <option value="__all">Tutte le categorie</option>
          </select>
        </label>
        {showingFiltered && selectedCategory !== '__all' ? (
          <span style={{ fontSize: '0.8rem', color: 'var(--sk-muted)' }}>
            Mostrati {filteredModules.length} moduli per «{CATEGORY_LABELS[selectedCategory] || selectedCategory}»
            {selectedCategory === tenantCategory ? ' (la tua categoria)' : ''}
          </span>
        ) : (
          <span style={{ fontSize: '0.8rem', color: 'var(--sk-muted)' }}>
            Tutti i {filteredModules.length} moduli disponibili
          </span>
        )}
      </div>

      {hasCategory && selectedCategory === tenantCategory ? (
        <div className="sk-saas-banner" style={{ fontSize: '0.85rem' }}>
          <strong>Moduli consigliati per la tua attività ({CATEGORY_LABELS[tenantCategory!]})</strong>
          <br />
          Questi sono i moduli più pertinenti per il tuo settore. Puoi esplorare altre categorie
          usando il selettore sopra, ma attenzione: attivare moduli di categorie diverse potrebbe
          non essere utile per la tua attività.
        </div>
      ) : null}

      {selectedCategory !== tenantCategory && selectedCategory !== '__all' && hasCategory ? (
        <div className="sk-saas-banner sk-saas-banner--danger" style={{ fontSize: '0.85rem' }}>
          <strong>Attenzione: stai visualizzando moduli di un'altra categoria.</strong>
          <br />
          La tua attività è registrata come «{CATEGORY_LABELS[tenantCategory!]}». Attivare moduli di
          altre categorie potrebbe non essere adatto al tuo settore.
        </div>
      ) : null}

      {error ? <p className="sk-error">{error}</p> : null}
      {message ? <p className="sk-ok">{message}</p> : null}

      {Object.entries(grouped).map(([pluginId, mods]) => (
        <div key={pluginId} style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--sk-text)' }}>
            {PLUGIN_LABELS[pluginId] || pluginId}
            {pluginId === 'support' ? (
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--sk-muted)', marginLeft: '0.5rem' }}>
                (trasversale)
              </span>
            ) : null}
            {pluginId === 'haccp' ? (
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--sk-muted)', marginLeft: '0.5rem' }}>
                (food-safety)
              </span>
            ) : null}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
            {mods.map((mod) => (
              <article
                key={mod.code}
                className="sk-saas-card"
                style={{
                  padding: '1rem',
                  border: mod.is_active ? '2px solid var(--sk-success, #16a34a)' : undefined,
                  opacity: mod.is_sellable ? 1 : 0.6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '1rem', margin: 0 }}>{mod.name}</h3>
                    <code style={{ fontSize: '0.75rem', color: 'var(--sk-muted)' }}>{mod.code}</code>
                  </div>
                  {mod.is_active ? (
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--sk-success, #16a34a)', textTransform: 'uppercase' }}>
                      Attivo
                    </span>
                  ) : null}
                </div>
                {mod.description ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--sk-muted)', marginTop: '0.5rem', lineHeight: 1.4 }}>
                    {mod.description}
                  </p>
                ) : null}
                <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {mod.price_monthly === 0 && mod.price_annual === 0 ? (
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Gratuito</span>
                    ) : (
                      <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        € {(cycle === 'annual' ? mod.price_annual : mod.price_monthly).toFixed(2)}
                        <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--sk-muted)' }}>
                          {' '}/{cycle === 'annual' ? 'anno' : 'mese'}
                        </span>
                      </span>
                    )}
                  </div>
                  {mod.is_active ? (
                    <button
                      type="button"
                      className="sk-btn sk-btn--quiet"
                      disabled={busy === mod.code}
                      onClick={() => void onCancel(mod)}
                    >
                      {busy === mod.code ? '…' : 'Disdici'}
                    </button>
                  ) : mod.is_sellable ? (
                    <button
                      type="button"
                      className="sk-btn"
                      disabled={busy === mod.code}
                      onClick={() => void onSubscribe(mod)}
                    >
                      {busy === mod.code ? '…' : 'Acquista'}
                    </button>
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: 'var(--sk-muted)' }}>Non in vendita</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}

      {filteredModules.length === 0 && !error ? (
        <p className="sk-saas-hint">Nessun modulo disponibile per questa categoria.</p>
      ) : null}
    </section>
  );
}
